import { SUBJECT_FIELD_SCHEMA } from "./fieldSchema";
import { COMP_REQUIRED_FIELDS } from "./ReadinessGate";
import { gridFromSubject, mergeGrid } from "./subjectGrid";
import type { CandidateComp, SubjectGrid } from "./types";
import type { CadLock } from "./api";
import type { CompSearchSummary } from "./compCriteria";

/**
 * THE SOURCE CASCADE, CLIENT SIDE — types and the pure rules that read its payload.
 *
 * The server half is `src/server/source_cascade.py` and the design is
 * `docs/product/SOURCE-CASCADE-SPEC.md` (owner-approved 2026-08-11). Read those first; nothing here
 * decides anything about sources. This file is the honest reader of one response shape, kept apart
 * from the screens so the four rules that are easy to get wrong on a render pass are written down
 * once, in functions a test can call directly:
 *
 *   1. "WE COULD NOT ASK" IS NOT "THERE IS NOTHING THERE". `blocking`/`could_not_ask` means a
 *      source was never reached — an outage, a timeout, a spent daily allowance. An agent told "no
 *      comparable sales found" over that goes hunting for a property that was always there. So
 *      `foundLine` REFUSES to produce a sentence while the cascade could not ask, and
 *      `couldNotAskBody` produces the different words instead. There is deliberately no code path
 *      in this file that can render an empty-result message during an outage.
 *
 *   2. PROVENANCE IS PER FIELD AND IT IS QUIET. `_field_source_labels[field]` is the string to
 *      print in the cell, verbatim — the server owns the wording, including the two owner-set agent
 *      tags ("Agent verified" / "Agent verified sales price"). This client invents no third wording
 *      and expands neither into a sentence. `cellProvenance` also withholds the tag when it would
 *      only repeat what the row's own Source column already says, because a tag on every cell is
 *      noise and the owner asked for the opposite of a big deal.
 *
 *   3. NOTHING HERE NAMES A PROVIDER. Every label — the rung names, the agent tags, the source
 *      strings — arrives in the payload (`rung_labels`, `agent_tags`, `_source`). The client
 *      hardcodes none of them, which is exactly the defect that left `ConfirmGrid`'s two
 *      `_source === "cotality"` branches dead for the life of the file: the server has never
 *      written that value.
 *
 *   4. A GAP IS A QUESTION. `gaps[]` is a list of things only the agent can supply, each with the
 *      server's own reason. It is not an error list, it stops nothing, and `CascadePanels` renders
 *      it in neutral type. See spec §4.
 */

// --------------------------------------------------------------------------------------------
// The wire shapes. Field-for-field from `source_cascade.cascade_resolve`'s response contract.
// --------------------------------------------------------------------------------------------

/** The five rung ids the server uses. Kept as a union for readability; every consumer below also
 * accepts a bare string, because a rung added server-side must not break a client that has not
 * been redeployed. */
export type RungId = "address" | "mls" | "cotality" | "cad" | "files" | "agent";

/**
 * THE LADDER, IN THE ORDER THE SERVER ASKS IN (owner-signed spec change, 2026-09-22 — slice S05).
 * `cad` is the county appraisal-district dataset this deployment already holds: a local read, free,
 * costing neither a provider allowance nor a model call. `files` means an UPLOAD, whose parse costs
 * one model call per document. They are two rungs because they have two prices, and uploads moved
 * BELOW Cotality because the old order let a file silently win over the live source.
 */
export const COMPS_LADDER: RungId[] = ["mls", "cotality", "cad", "files", "agent"];
export const SUBJECT_LADDER: RungId[] = ["address", "mls", "cotality", "cad", "files", "agent"];

/**
 * The five rung statuses. The distinction that matters is the last two:
 *   `unavailable`   — WE COULD NOT ASK. Outage, timeout, spent allowance. Never "nothing found".
 *   `not_available` — the rung does not exist here. The soft pass, and it is silent by design.
 */
export type RungStatus = "ok" | "empty" | "no_match" | "unavailable" | "not_available";

export interface CascadeRung {
  rung: RungId | string;
  label: string;
  status: RungStatus | string;
  count: number;
  reason: string | null;
  reason_code: string | null;
  [k: string]: unknown;
}

export interface CascadeGap {
  scope: "subject" | "comp" | string;
  field: string;
  filled_by: string;
  filled_by_label: string;
  why: string;
  comp_key?: string;
  comp_name?: string | null;
}

/** `kind` is the branch point. `not_real_data` is the sandbox warning and is the loudest thing in
 * the payload — `env: sandbox` is live in production right now, so it fires on every real run. */
export interface CascadeNotice {
  kind: string;
  severity: string;
  text: string;
}

/** The one notice kind that is about TRUTH rather than paperwork, named so no screen has to spell
 * it. `env: sandbox` is live in production right now, so this fires on every real run until the
 * owner flips `VALUBRIEF_COTALITY_ENV`. Sandbox records are complete, plausible, and possibly about
 * a different house — the agent must not read them as findings. */
export const NOT_REAL_DATA = "not_real_data";

export interface CotalityGate {
  installed: boolean;
  enabled: boolean;
  configured: boolean;
  /** Null while dormant (the gate says only that it is parked). */
  env: string | null;
  production_data: boolean;
  /** Non-null whenever the data is NOT real. The client shows it; it never decides it. */
  sandbox_note: string | null;
  deliverable_license: boolean;
  not_deliverable_note: string | null;
  /** Null while dormant: nothing is counted, and unknown is not zero. */
  daily_cap: number | null;
  calls_today: number | null;
  calls_remaining: number | null;
  /**
   * IS IT LIVE, NOT JUST INSTALLED (slice S05). `enabled` and `configured` above answer "did the
   * wiring ship?" — and on 2026-09-22 both answered yes while a real call came back 502 with
   * Cloudflare's error page in place of our JSON. These answer the question a screen actually has
   * before it offers a control.
   *
   * Optional because an older server does not send them. A client that cannot tell must not
   * ASSUME offline — that would put a service notice on a healthy deployment — so every reader
   * below treats `undefined` as "no claim either way".
   */
  available?: boolean;
  /** "live" | "offline" | "disabled". Widened to string: the server owns this vocabulary. */
  state?: string;
  state_reason?: string;
  state_reason_code?: string;
}

/** The three states `cotality.availability()` can report. */
export type CotalityState = "live" | "offline" | "disabled" | "dormant";

/**
 * Is the public-records provider LIVE? `null` when the server did not say.
 *
 * NULL IS NOT FALSE, and the distinction is the whole reason this returns three values. A client
 * talking to a server that predates `state` knows nothing; painting "Cotality is offline" on that
 * would be a claim nobody made — the same dishonesty as an "all systems normal" banner, pointing
 * the other way.
 */
export function cotalityLive(gates: CascadeGates | null | undefined): boolean | null {
  return cotalityStateLive(gates?.cotality);
}

/** The same question asked of `/api/cotality/status`'s body, which carries the two fields without
 * the rest of a gate. Deliberately structural rather than a cast: a surface that only has the
 * status route should not have to pretend it holds a whole `CascadeGates`. */
export function cotalityStateLive(
  state: { available?: boolean; state?: string } | null | undefined
): boolean | null {
  if (!state) return null;
  if (typeof state.available === "boolean") return state.available;
  if (typeof state.state === "string") return state.state === "live";
  return null;
}

/**
 * The one calm line for a surface that is not the CMA flow — `/app`'s service-notice slot.
 * Returns null when Cotality is live, when the server said nothing, and when the connector is
 * merely switched off on a deployment that never intended to use it.
 *
 * SENTENCE CASE, NO ALARM, NO FLAG NAME. An agent cannot act on `VALUBRIEF_COTALITY_ENABLED`, and
 * naming an internal switch on a customer surface is the same mistake `INTERNAL_ONLY_KINDS` in
 * `CascadePanels.tsx` exists to correct.
 */
export const COTALITY_OFFLINE_HOME_LINE =
  "Public-records lookup is offline right now. County CAD data still works, and you can enter a " +
  "subject's details yourself.";

/**
 * The body a Cotality route returns when it could not ask the provider — HTTP 409, and a body an
 * edge proxy passes through. See `cotality.CotalityOffline` for why 409 and not 502/503/504.
 */
export interface CotalityOfflineBody {
  code: string;
  cad_required: boolean;
  /** The sentence to print. Server-authored and owner-set; never re-worded on this side. */
  message: string;
  /** One short line naming how to provide the data. */
  how?: string | null;
  state?: string;
  cad?: Record<string, unknown> | null;
}

/**
 * Pull the offline envelope out of a thrown `ApiError`, or null when this is any other failure.
 *
 * STRUCTURAL, NOT AN `instanceof`. `lib/auth.ts` attaches the parsed body to the error as `body`;
 * checking for the shape rather than the class keeps this readable from a test that throws a
 * plain object, and keeps `cascade.ts` free of an import from the auth layer.
 */
export function offlinePayload(err: unknown): CotalityOfflineBody | null {
  const body = (err as { body?: unknown } | null)?.body;
  if (!body || typeof body !== "object") return null;
  const b = body as Record<string, unknown>;
  if (typeof b.code !== "string" || !b.code.startsWith("cotality_")) return null;
  if (typeof b.message !== "string") return null;
  return {
    code: b.code,
    cad_required: b.cad_required === true,
    message: b.message,
    how: typeof b.how === "string" ? b.how : null,
    state: typeof b.state === "string" ? b.state : undefined,
    cad: (b.cad ?? null) as Record<string, unknown> | null,
  };
}

export function cotalityServiceLine(
  state: { available?: boolean; state?: string } | null | undefined
): string | null {
  if (!state) return null;
  if (cotalityStateLive(state) !== false) return null;
  // `disabled` is a deployment that has not switched the connector on. Nothing is broken and
  // nothing is degraded from what this operator asked for, so there is nothing to report.
  if (state.state === "disabled") return null;
  // `dormant` is the operator parking it on purpose (owner, 2026-10-20). Not an outage either.
  if (state.state === "dormant") return null;
  return COTALITY_OFFLINE_HOME_LINE;
}

export interface MlsGate {
  available: boolean;
  provider: string | null;
  implemented: boolean;
  reason: string;
}

export interface CascadeGates {
  cotality: CotalityGate;
  mls: MlsGate;
}

export interface AgentTags {
  default: string;
  sale_price: string;
}

/** `GET /api/next/cascade/status` — gates only. No external call, no daily-cap spend, so it is safe
 * to fetch before the agent has typed anything. */
export interface CascadeStatus {
  gates: CascadeGates;
  /** True only for the provider-diagnostics allow-list (owner, 2026-10-19; server-enforced in
   * `provider_diag`). Absent from an older server: read as false. */
  provider_diagnostics?: boolean;
  rungs: { subject: { rung: string; label: string }[]; comps: { rung: string; label: string }[] };
  rung_labels: Record<string, string>;
  agent_tags: AgentTags;
  min_comps: number;
}

/** Any resolved row — the subject or one comp — with the two provenance maps the merge attaches. */
export interface ProvenancedRow {
  /** The ROW-level tag, unchanged in meaning from before the cascade existed. */
  _source?: string;
  /** `{field: rung_id}` — machine-readable, the thing to branch on. */
  _field_sources?: Record<string, string>;
  /** `{field: label}` — exactly what to print in the cell, server-worded. */
  _field_source_labels?: Record<string, string>;
}

export type CascadeSubject = Record<string, unknown> & ProvenancedRow;
export type CascadeComp = Record<string, unknown> & ProvenancedRow;

/** The MLS rung's report (server: `mls_rung.fetch_comps`). PRESENT ONLY when the rung ran. The
 * competing-listings search is reported on its own: whether it ran, the radius it ACTUALLY covered
 * (never the sold search's `radius_mi`), and how many it found. */
export interface CascadeMlsResult {
  status?: string;
  reason?: string | null;
  radius_mi?: number | null;
  months?: number | null;
  widened?: boolean;
  actives_searched?: boolean;
  actives_radius_mi?: number | null;
  actives_found?: number | null;
  actives_reason?: string | null;
  [k: string]: unknown;
}

export interface CascadeResolveResult {
  address: string | null;
  clip: string | null;
  /** Engine-shaped — the SAME shape `POST /api/amb/jobs` already takes. */
  subject: CascadeSubject;
  comps: CascadeComp[];
  subject_ladder: CascadeRung[];
  comp_ladder: CascadeRung[];
  answered_by: { subject: string | null; comps: string | null };
  gaps: CascadeGap[];
  /** THE OUTAGE CHANNEL. Non-empty means a source was never asked. */
  blocking: CascadeRung[];
  could_not_ask: boolean;
  warnings: string[];
  notices: CascadeNotice[];
  gates: CascadeGates;
  rung_labels: Record<string, string>;
  agent_tags: AgentTags;
  min_comps: number;
  comp_count: number;
  requires_review: boolean;
  requires_review_reason: string;
  /** One row per outside-provider rung, sent ONLY to the provider-diagnostics allow-list (owner,
   * 2026-10-19; `provider_diag.diagnostics`). Absent for everyone else. */
  provider_diagnostics?: ProviderDiagnostic[];
  /**
   * TRUE ONLY WHEN NOTHING AUTOMATIC COULD ANSWER AND NO UPLOAD WAS SUPPLIED (slice S05). Not
   * "Cotality is offline" on its own: if the county record answered, the agent has what they need
   * and a notice about a provider they never asked for is noise. The soft pass stays silent.
   *
   * Optional — an older server omits it, and `undefined` must read as "no claim", never as true.
   */
  cad_required?: boolean;
  /**
   * NTREIS MLS competition (Release 1.1). PRESENT ONLY when the server's MLS rung ran for this
   * user (an Activated NTREIS group member); absent for everyone else, so this flow is unchanged
   * with the flag off. Active/pending listings: they travel in `meta.actives` and never in `comps`.
   */
  mls_actives?: CascadeComp[];
  /**
   * COMPS-UX (2026-10-25). The MLS sales the SAME pull fetched that the criteria set aside, so the
   * panel can widen or narrow over them in the browser with no second MLS round trip. Present only
   * when the MLS rung ran. HELD IN THE PANEL'S MEMORY FOR THE WORKING SESSION ONLY: it is stripped
   * from `CascadeMeta` below, and a held row that a filter brings into `comps` carries `_held_mls`,
   * which `draftStore.saveDraft` removes before any copy (session, durable, server) is written. NTREIS storage and display terms
   * for held rows are not confirmed (flagged in the slice report).
   */
  held_back?: CascadeComp[];
  /** The MLS rung's own report, including whether and how far competing homes were searched. */
  mls?: CascadeMlsResult;
  /** The exact sentence to print, and the one line that says how to unblock it. Server-authored. */
  cad_notice?: CascadeCadNotice | null;
  /** Was reading an uploaded document worth its cost, given what the free rungs resolved? */
  file_parse?: CascadeFileParse;
  /** 1G — the scope searched, the widen sentence, and per-source found / matched counts. Present
   * whenever comps were requested; absent from an older server. */
  comp_search?: CompSearchSummary;
}

/** The blocking notice for "Cotality is offline. CAD data is needed to proceed." */
export interface CascadeCadNotice {
  kind: string;
  severity: string;
  code: string;
  /** The owner-set sentence. Printed verbatim; this client words nothing. */
  text: string;
  /** One short line naming how to provide the data. */
  how: string;
}

/**
 * The upload-parse decision. The cost is PER DOCUMENT READ, not per field — `amb_extract` sends
 * one model call over the whole document and asks for every field at once — so `parse` is a
 * boolean and `gap_fields` is what it was decided on, never a field filter.
 */
export interface CascadeFileParse {
  parse: boolean;
  gap_fields: string[];
  gap_count: number;
  has_upload: boolean;
  reason: string;
}

/**
 * Everything in a resolve EXCEPT the two payloads the flow immediately consumes into the subject
 * grid and the comp table.
 *
 * Held (and persisted with the draft) rather than the whole result, because keeping `subject` and
 * `comps` around after they have been merged into the screen creates a second copy of the facts the
 * agent is editing — and a second copy is a thing that can disagree with the first. What the later
 * steps actually need from a resolve is the reporting: which rung answered, what could not be
 * asked, what is still missing, and the label strings.
 */
export interface ProviderDiagnostic {
  /** Present on the per-resolve rows; absent on the admin route's per-provider rows. */
  scope?: "subject" | "comps";
  rung: string;
  label?: string | null;
  status: string;
  reason_code?: string | null;
  /** ISO-8601 UTC of the provider's last successful answer for this organization; null if none. */
  last_answer_at?: string | null;
}

/**
 * WHAT A REGULAR USER IS TOLD WHEN NO COMPARABLE SALES CAME BACK. The owner's two sentences,
 * verbatim (fan-out item 1F, 2026-10-20): no provider named, no reason code, no "try again".
 */
export const NO_COMPS_MLS_CONNECTED =
  "No recent sales matched this address. Adjust your search criteria or upload a comp file to " +
  "keep going.";
export const NO_COMPS_NOT_CONNECTED =
  "Add comparable sales to keep going. Adjust your search criteria, upload a comp file, or " +
  "request MLS access and we'll pull recent sales for you automatically.";

export function noCompsCopy(mlsConnected: boolean): string {
  return mlsConnected ? NO_COMPS_MLS_CONNECTED : NO_COMPS_NOT_CONNECTED;
}

/**
 * Is the public-records connector DORMANT (owner, 2026-10-20: `VALUBRIEF_COTALITY_OFFLINE=1`)?
 * Then no screen asks it anything: the lookup is skipped, not attempted and refused.
 */
export function publicRecordsDormant(gates: CascadeGates | null | undefined): boolean {
  return gates?.cotality?.state === "dormant";
}

export type CascadeMeta = Omit<CascadeResolveResult, "subject" | "comps" | "held_back">;

export function cascadeMeta(result: CascadeResolveResult): CascadeMeta {
  const { subject: _subject, comps: _comps, held_back: _held, ...meta } = result;
  return meta;
}

/** The request body. Everything except the address is optional, and omitting all of it is the
 * NORMAL path — the soft pass, which produces no warning. */
export interface CascadeResolveArgs {
  address?: string;
  clip?: string;
  agent_subject?: Record<string, unknown>;
  file_subject?: Record<string, unknown>;
  agent_comps?: Record<string, unknown>[];
  file_comps?: Record<string, unknown>[];
  comp_params?: Record<string, unknown>;
  /** 1G — the comp search criteria (`compSearch.criteriaToRequest`). Absent = the default scope. */
  comp_criteria?: Record<string, unknown>;
  want_comps?: boolean;
  /** Q30: ask the MLS current-listings search ONLY (no provider lookup, no sold re-search). Sent by
   * the competing-homes Refresh; absent = the whole cascade, as before. */
  listings_only?: boolean;
  /** An attached file that has NOT been read yet. Its content is never sent here — the flag only
   * lets the resolver price reading it (`file_parse`). Owner direction, 2026-09-22. */
  upload_pending?: boolean;
  /** THE LOCK OF A PICKED COUNTY RECORD (`cadPick.CadPick.lock`). Sent on EVERY cascade call while
   * a pick exists: the server then reads the subject from that record alone and never asks
   * Cotality for it (`source_cascade._resolve_inner`). Provenance veto, 2026-10-04. */
  cad_pick?: CadLock;
}

/** `args` with the CURRENT pick's lock, or without any lock when there is no pick — a retry of an
 * older request never carries a lock the agent has since changed or cleared. */
export function cascadeArgsWithPick(
  args: CascadeResolveArgs, lock: CadLock | null | undefined,
): CascadeResolveArgs {
  const { cad_pick: _stale, ...rest } = args;
  return lock ? { ...rest, cad_pick: lock } : rest;
}

/**
 * The notices a screen can raise BEFORE anything has been resolved, built from `/status`'s gates.
 *
 * A deliberate mirror of `source_cascade._gate_notices`, minus its one condition: that function
 * only speaks when Cotality actually contributed to a result, which is the right rule for a
 * finished resolve and the wrong one for the screen where the agent is about to ask for one. If the
 * data this deployment is about to fetch is not real, that is worth saying before they type an
 * address, not after they have read a grid of figures. Same kinds, same severities, same server-
 * authored text — nothing is worded here.
 */
export function noticesFromGates(gates: CascadeGates | null | undefined): CascadeNotice[] {
  const out: CascadeNotice[] = [];
  const cot = gates?.cotality;
  if (!cot) return out;
  if (cot.sandbox_note) out.push({ kind: NOT_REAL_DATA, severity: "critical", text: cot.sandbox_note });
  if (cot.not_deliverable_note) {
    out.push({ kind: "not_deliverable", severity: "warning", text: cot.not_deliverable_note });
  }
  return out;
}

// --------------------------------------------------------------------------------------------
// Per-field provenance
// --------------------------------------------------------------------------------------------

export const FIELD_SOURCES_KEY = "_field_sources";
export const FIELD_SOURCE_LABELS_KEY = "_field_source_labels";

/** Which RUNG produced this field — the machine token (`agent`, `cotality`, `files`, …). This is
 * what a per-cell decision branches on. Never the label: labels are prose and change. */
export function fieldSource(row: ProvenancedRow | null | undefined, field: string): string | null {
  const src = row?._field_sources?.[field];
  return typeof src === "string" && src ? src : null;
}

/** The string to PRINT next to this field, exactly as the server worded it. */
export function fieldSourceLabel(row: ProvenancedRow | null | undefined, field: string): string | null {
  const label = row?._field_source_labels?.[field];
  return typeof label === "string" && label ? label : null;
}

/**
 * The quiet per-cell tag, or null when there is nothing worth saying.
 *
 * WITHHELD WHEN IT WOULD ONLY REPEAT THE ROW. A comp read whole from one source has every field
 * tagged with that source, and the row already carries it in the Source column — printing it again
 * on eighteen cells is the "big deal" the owner explicitly did not want. The tag earns its place
 * exactly when the field disagrees with the row: an agent-entered sale price on a public-record
 * row, a public-record lot size on an uploaded row. Compared by LABEL rather than by rung id
 * because `_source` is a label and the two are the same vocabulary — and because that comparison
 * hardcodes no provider string.
 */
export function cellProvenance(row: ProvenancedRow | null | undefined, field: string): string | null {
  const label = fieldSourceLabel(row, field);
  if (!label) return null;
  const rowLabel = typeof row?._source === "string" ? row._source.trim() : "";
  return rowLabel && rowLabel === label ? null : label;
}

/**
 * Is this row a provider candidate — the kind the server refuses to build from unless a human has
 * explicitly ticked it (`amb_report._reject_unreviewed_provider_comps`)?
 *
 * Answered by comparing the row's own `_source` against the cotality rung's label AS THE PAYLOAD
 * REPORTS IT. `ConfirmGrid` used to ask `c._source === "cotality"` in two places; the server has
 * never written that value (`cotality.SOURCE_TAG` is "Cotality (public-record aggregate)"), so both
 * branches were dead for the life of the file and neither has ever fired on real data. Without a
 * `rung_labels` in hand this returns FALSE — "I cannot tell" must not be answered as "yes", because
 * the only thing this gates is an auto-selection the agent can make themselves.
 */
export function isProviderRow(
  row: { _source?: string } | null | undefined,
  rungLabels: Record<string, string> | null | undefined
): boolean {
  const providerLabel = rungLabels?.cotality;
  if (!providerLabel) return false;
  return typeof row?._source === "string" && row._source === providerLabel;
}

/** True when this FIELD came from the provider, whoever the row belongs to. The per-cell half of
 * the same question, and the one that survives a row assembled from three rungs. */
export function isProviderField(row: ProvenancedRow | null | undefined, field: string): boolean {
  return fieldSource(row, field) === "cotality";
}

// --------------------------------------------------------------------------------------------
// "We could not ask" — rule 1, in code
// --------------------------------------------------------------------------------------------

/** The rungs that were never reached. Empty on every ordinary run, including the soft pass. */
export function couldNotAsk(meta: Pick<CascadeMeta, "blocking"> | null | undefined): CascadeRung[] {
  return (meta?.blocking ?? []).filter((r) => !!r);
}

/** Owner-facing heading for the outage state. Deliberately NOT a sentence about comparable sales:
 * the failure is ours and it is about asking, not about what exists. */
export const COULD_NOT_ASK_TITLE = "We couldn't ask one of our sources";

/**
 * The paragraph that has to appear beside it, and the reason this whole distinction is enforced in
 * a helper rather than left to a render branch. Nothing about the property has been ruled out.
 */
export const COULD_NOT_ASK_BODY =
  "Nothing has been ruled out. This is not the same as finding no sales — we never got an answer " +
  "back, so there may well be comparable sales here. Try again in a moment, or add your own file.";

/** Reason codes whose "Try again" cannot work until something outside this screen changes: the
 * connector is held offline (`not_connected`), switched off or unconfigured. The server also marks
 * these (and an open breaker) `retryable: false` (2026-10-18); the list covers an older server. */
const NOT_RETRYABLE = new Set(["not_connected", "disabled", "unconfigured"]);

/** Would pressing "Try again" now have any chance of a different answer? */
export function canRetry(meta: Pick<CascadeMeta, "blocking"> | null | undefined): boolean {
  return couldNotAsk(meta).some(
    (r) => r.retryable !== false && !NOT_RETRYABLE.has(String(r.reason_code ?? "")));
}

/** The body beside the title when no retry can help: the same "nothing ruled out" fact, and the
 * one action that does work. */
export const COULD_NOT_ASK_BODY_NO_RETRY =
  "Nothing has been ruled out. This is not the same as finding no sales — that source was never " +
  "asked, and asking again now would get the same answer. Add your own comps file to continue.";

/** Each blocked rung's own reason, deduplicated, in the server's words. */
export function couldNotAskReasons(meta: Pick<CascadeMeta, "blocking"> | null | undefined): string[] {
  const out: string[] = [];
  for (const rung of couldNotAsk(meta)) {
    const reason = typeof rung.reason === "string" ? rung.reason.trim() : "";
    if (reason && !out.includes(reason)) out.push(reason);
  }
  return out;
}

// --------------------------------------------------------------------------------------------
// "Say what we found and where it came from, in ONE plain line" — spec §4
// --------------------------------------------------------------------------------------------

/** Where the comparables came from, as a phrase that fits inside the sentence below. Derived from
 * `answered_by.comps`, so a rung that lights up later needs no change here beyond one entry. */
export function compsSourcePhrase(
  meta: Pick<CascadeMeta, "answered_by"> | null | undefined
): string {
  switch (meta?.answered_by?.comps) {
    case "cotality":
      return "from public records";
    case "files":
      return "in the file you added";
    case "mls":
      return "from the MLS";
    case "agent":
      return "you entered";
    default:
      return "";
  }
}

/**
 * The one plain line. "We found 8 nearby sales from public records. Check the ones you want to use."
 *
 * Returns null — rather than a "we found none" sentence — when the cascade could not ask, which is
 * rule 1 made structural: there is no argument to this function that produces an empty-result
 * message during an outage.
 */
export function foundLine(
  meta: Pick<CascadeMeta, "answered_by" | "blocking"> | null | undefined,
  compCount: number
): string | null {
  if (!meta) return null;
  if (couldNotAsk(meta).length > 0) return null;
  if (compCount <= 0) return null;
  const where = compsSourcePhrase(meta);
  const sales = compCount === 1 ? "sale" : "sales";
  const which = compCount === 1 ? "one" : "ones";
  return `We found ${compCount} nearby ${sales}${where ? ` ${where}` : ""}. ` +
    `Check the ${which} you want to use.`;
}

/**
 * The honest empty answer — and it is only ever reachable when every source ANSWERED. A rung that
 * could not be asked returns null here and the outage panel renders instead.
 */
export function noSalesLine(
  meta: Pick<CascadeMeta, "answered_by" | "blocking"> & { comp_ladder?: CascadeRung[] } | null | undefined,
  compCount: number
): string | null {
  if (!meta) return null;
  if (couldNotAsk(meta).length > 0) return null;
  if (compCount > 0) return null;
  // "WE CHECKED" MUST BE TRUE (2026-10-18). With every comp source soft-passed -- Cotality not
  // connected, no MLS, no file -- nothing was checked, and "found no sales" would turn an unknown
  // into a "no". Only a rung that ANSWERED earns the sentence; `notConnectedLine` says the
  // true thing otherwise. An older payload with no ladder keeps the previous behavior.
  if (Array.isArray(meta.comp_ladder)
      && !meta.comp_ladder.some((r) => r.status === "empty" || r.status === "ok")) {
    return null;
  }
  return "We checked and found no recent nearby sales for this address. Add your own file, or " +
    "widen the search by entering sales you know about.";
}

/**
 * Cotality is switched off or not configured here, and nothing else supplied a sale. The soft pass
 * stays silent when another source answered; when NOTHING did, the screen says why it is empty
 * instead of saying nothing, or saying "no sales" (2026-10-18, owner).
 */
export function notConnectedLine(
  meta: Pick<CascadeMeta, "blocking"> & { comp_ladder?: CascadeRung[] } | null | undefined,
  compCount: number
): string | null {
  if (!meta || compCount > 0 || couldNotAsk(meta).length > 0) return null;
  const ladder = Array.isArray(meta.comp_ladder) ? meta.comp_ladder : [];
  if (ladder.some((r) => r.status === "ok" || r.status === "empty")) return null;
  const cot = ladder.find((r) => r.rung === "cotality");
  if (!cot || !["disabled", "unconfigured"].includes(String(cot.reason_code ?? ""))) return null;
  return "Cotality is not connected on this account, so no public-record sales were looked up. " +
    "Add your own comps file to continue.";
}

// --------------------------------------------------------------------------------------------
// Gaps — questions, never errors
// --------------------------------------------------------------------------------------------

/** The agent's word for a gap's field. Subject fields come from the grid schema, comp fields from
 * the comp-required list, so a label can never disagree with the row the agent has to type into. */
export function gapLabel(gap: CascadeGap): string {
  const fromSubject = SUBJECT_FIELD_SCHEMA.find((f) => f.key === gap.field);
  if (fromSubject) return fromSubject.label;
  const fromComp = COMP_REQUIRED_FIELDS.find((f) => f.key === gap.field);
  if (fromComp) return fromComp.label;
  return EXTRA_GAP_LABELS[gap.field] ?? gap.field;
}

/** The handful of gap fields neither list names, because neither the subject grid nor the comp
 * required set owns them. `condition` is the human judgment; the bath split is asked per comp. */
const EXTRA_GAP_LABELS: Record<string, string> = {
  condition: "Condition",
  full_baths: "Full baths",
  half_baths: "Half baths",
  name: "Address",
};

export function subjectGaps(gaps: CascadeGap[] | null | undefined): CascadeGap[] {
  return (gaps ?? []).filter((g) => g.scope === "subject");
}

/**
 * Comp gaps rolled up BY FIELD, because the list an agent can act on is "four sales are missing a
 * garage count", not forty lines of the same sentence. The comp names are carried so the roll-up
 * still says which rows, and the server's `why` is kept verbatim.
 */
export interface CompGapGroup {
  field: string;
  label: string;
  why: string;
  comps: string[];
}

export function compGapGroups(gaps: CascadeGap[] | null | undefined): CompGapGroup[] {
  const byField = new Map<string, CompGapGroup>();
  for (const gap of gaps ?? []) {
    if (gap.scope !== "comp") continue;
    let group = byField.get(gap.field);
    if (!group) {
      group = { field: gap.field, label: gapLabel(gap), why: gap.why, comps: [] };
      byField.set(gap.field, group);
    }
    const name = (gap.comp_name ?? "").trim();
    if (name && !group.comps.includes(name)) group.comps.push(name);
  }
  return [...byField.values()];
}

// --------------------------------------------------------------------------------------------
// Turning a resolve into what the existing screens already know how to render
// --------------------------------------------------------------------------------------------

/**
 * The resolved subject as a confirm grid, EVERY ROW TAGGED WITH THE RUNG THAT ACTUALLY PRODUCED IT.
 *
 * The fallback when a field carries no per-field label is the ROW's `_source` and then a plain
 * "Public records" — never a guess at a provider name, and never a tag that claims the agent said
 * something they did not.
 */
export function gridFromCascadeSubject(subject: CascadeSubject): SubjectGrid {
  return gridFromSubject(subject, (key) => subjectFieldTag(subject, key));
}

/** Fill only the rows the agent has NOT confirmed, with the same per-field tags. Used when the
 * cascade runs after an upload has already populated the grid: a lower rung fills blanks and can
 * never overwrite a value a human has ticked, which is the client half of the server's own rule. */
export function mergeCascadeSubject(current: SubjectGrid, subject: CascadeSubject): SubjectGrid {
  return mergeGrid(current, subject, (key) => subjectFieldTag(subject, key));
}

/** The rung id `source_cascade` credits a Cotality figure to (`RUNG_COTALITY`). */
const COTALITY_RUNG = "cotality";

/**
 * A resolved subject with every Cotality-sourced field removed — value and both provenance maps.
 *
 * THE CLIENT WALL FOR A CAD-LOCKED SUBJECT (provenance veto, 2026-10-04). The owner's rule is "If
 * the agent picked a CAD row, we never call Cotality at all", and the server enforces it when it
 * receives the lock. This is the second wall: whatever a server answers (an older one, or one that
 * never saw the lock), a field the record did not state stays a gap the agent fills ("You
 * entered") and is never a Cotality value. Never adds or rewrites a value.
 */
export function subjectWithoutProviderFields(subject: CascadeSubject): CascadeSubject {
  const sources = subject._field_sources ?? {};
  const drop = Object.keys(sources).filter((k) => sources[k] === COTALITY_RUNG);
  if (!drop.length) return subject;
  const out: CascadeSubject = { ...subject };
  const fs = { ...sources };
  const fl = { ...(subject._field_source_labels ?? {}) };
  for (const k of drop) {
    delete out[k];
    delete fs[k];
    delete fl[k];
  }
  out._field_sources = fs;
  out._field_source_labels = fl;
  return out;
}

function subjectFieldTag(subject: CascadeSubject, key: string): string {
  return fieldSourceLabel(subject, key)
    ?? (typeof subject._source === "string" && subject._source ? subject._source : "Public records");
}

/**
 * The resolved comps as review-grid candidates.
 *
 * NOTHING ARRIVES TICKED. Every row here is a candidate — the server says so itself
 * (`requires_review: true`) and refuses to generate from an unreviewed provider candidate. The
 * provenance maps ride along untouched so the grid can tag per cell and so the submit sends them
 * back exactly as they came (the engine reads property fields by name and ignores the rest).
 */
export function compsFromCascade(comps: CascadeComp[] | null | undefined): CandidateComp[] {
  return (comps ?? []).map((c) => ({ ...(c as unknown as CandidateComp), _selected: false, confirmed: false }));
}

/** The MLS pull's held-back rows, marked memory-only (`_held_mls`): `draftStore.saveDraft` and the
 * wire strip the mark's rows and the mark itself. */
export function heldRowsFromCascade(held: CascadeComp[] | null | undefined): CandidateComp[] {
  return compsFromCascade(held).map((c) => ({ ...c, _held_mls: true }));
}

/**
 * The subject fields the AGENT is the source of — the only thing that belongs in `agent_subject`.
 *
 * WHY THIS IS NOT "EVERY CONFIRMED FIELD". The cascade credits a field to the most ORIGINAL rung
 * that states it, so handing back a public-record figure the agent merely ticked would be harmless
 * on its own — but `resolve` also skips the subject lookup entirely once the subject looks
 * complete, and a complete subject assembled from echoed provider values would then be tagged
 * "Agent verified" end to end. The agent did not source those figures; they agreed with them.
 *
 * So only two kinds of value are sent: one the agent typed from scratch ("You entered"), and one
 * they corrected away from what a source said (`edited`). Everything else is left for the cascade
 * to state for itself — which costs nothing, because the step-1 lookup has already primed the
 * connector's own cache under the same key.
 */
export function agentSubjectFromGrid(grid: SubjectGrid | null | undefined): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const [key, field] of Object.entries(grid ?? {})) {
    if (!field?.checked || field.value === null || field.value === undefined || field.value === "") continue;
    const typedByHand = !field.sourceTag || field.sourceTag === "You entered";
    if (typedByHand || field.edited) out[key] = field.value;
  }
  return out;
}
